import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { houseLabelKeyForPath } from '@/application/navigation/menuHouses';
import { Icon } from '@/presentation/components/common/Icon';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';
import { LoadingSpinner } from '@/presentation/components/common/LoadingSpinner';

const LOCK_ICON = 'M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2zM7 11V7a5 5 0 0 1 10 0v4';

export type PanelPageStateVariant = 'loading' | 'empty' | 'error' | 'forbidden';

/** Dominios con copy forbidden específico (i18n). */
export type PanelPageStateDomain =
  | 'default'
  | 'dashboard'
  | 'finances'
  | 'patients'
  | 'professionals'
  | 'clinics'
  | 'team';

interface PanelPageStateProps {
  variant: PanelPageStateVariant;
  title?: string;
  message?: string;
  /** Clave i18n para mensaje forbidden por dominio (ej. domain.finances.forbidden.message). */
  domain?: PanelPageStateDomain;
  onRetry?: () => void;
  retryLabel?: string;
  homeTo?: string | null;
  /** inline = bloque dentro de página; page = ocupa zona principal */
  layout?: 'inline' | 'page';
  action?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

function shellClass(variant: PanelPageStateVariant, layout: 'inline' | 'page'): string {
  const base = layout === 'page' ? 'panel-page-state panel-page-state--page' : 'panel-page-state panel-page-state--inline';
  return `${base} panel-page-state--${variant}`;
}

export function PanelPageState({
  variant,
  title,
  message,
  domain = 'default',
  onRetry,
  retryLabel,
  homeTo,
  layout = 'inline',
  action,
  className = '',
  'data-testid': testId,
}: PanelPageStateProps) {
  const { t } = useTranslation();

  if (variant === 'loading') {
    return (
      <div className={className} data-testid={testId}>
        <LoadingBlock
          label={message ?? t('common.pageStates.loading.message')}
          minHeightClassName={layout === 'page' ? 'min-h-[12rem]' : 'min-h-[8rem]'}
        />
      </div>
    );
  }

  const resolvedTitle =
    title ??
    (variant === 'forbidden'
      ? t('common.pageStates.forbidden.title')
      : t(`common.pageStates.${variant}.title`));

  const forbiddenMessageKey: Record<PanelPageStateDomain, string> = {
    default: 'common.pageStates.forbidden.message',
    dashboard: 'dashboard.forbidden.message',
    finances: 'domain.finances.forbidden.message',
    patients: 'domain.patients.forbidden.message',
    professionals: 'domain.professionals.forbidden.message',
    clinics: 'domain.clinics.forbidden.message',
    team: 'team.forbidden.message',
  };

  let resolvedMessage = message;
  if (!resolvedMessage) {
    if (variant === 'forbidden') {
      resolvedMessage = t(forbiddenMessageKey[domain], {
        defaultValue: t('common.pageStates.forbidden.message'),
      });
    } else {
      resolvedMessage = t(`common.pageStates.${variant}.message`);
    }
  }

  const retry =
    variant === 'error' && onRetry ? (
      <button
        type="button"
        onClick={onRetry}
        className="panel-page-state__retry btn btn-secondary btn-sm"
      >
        {retryLabel ?? t('common.pageStates.error.retry')}
      </button>
    ) : null;

  const homeLink =
    variant === 'forbidden' && homeTo ? (
      <Link to={homeTo} className="panel-page-state__cta">
        {forbiddenHomeLabel(homeTo, t)}
      </Link>
    ) : null;

  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      aria-live={variant === 'error' ? 'assertive' : 'polite'}
      data-testid={testId ?? `panel-page-state-${variant}`}
      className={`${shellClass(variant, layout)} ${className}`.trim()}
    >
      {variant === 'empty' ? (
        <span className="panel-page-state__icon" aria-hidden>
          ○
        </span>
      ) : null}
      {variant === 'error' ? (
        <span className="panel-page-state__icon panel-page-state__icon--error" aria-hidden>
          !
        </span>
      ) : null}
      {variant === 'forbidden' ? (
        <span className="panel-page-state__icon panel-page-state__icon--forbidden" aria-hidden>
          <Icon path={LOCK_ICON} className="h-7 w-7" />
        </span>
      ) : null}
      <div className="panel-page-state__body">
        <p className="panel-page-state__title">{resolvedTitle}</p>
        <p className="panel-page-state__message">{resolvedMessage}</p>
        {(action || retry || homeLink) && (
          <div className="panel-page-state__actions">
            {action}
            {retry}
            {homeLink}
          </div>
        )}
      </div>
    </div>
  );
}

function forbiddenHomeLabel(homeTo: string, t: (key: string, opts?: { section?: string }) => string): string {
  const labelKey = houseLabelKeyForPath(homeTo);
  if (labelKey && labelKey !== 'menu.summary') {
    return t('common.pageStates.forbidden.goTo', { section: t(labelKey) });
  }
  return t('common.pageStates.forbidden.backHome');
}

/** Spinner compacto para botones / filas (re-export util). */
export { LoadingSpinner };
